import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AppService } from 'src/app/app.service';
import { CompanyService } from 'src/app/company/company.service';
import { Role } from 'src/app/shared/models/user-model';
import { PrfTemplateDownloadService } from './prf-template-download.service';

type TemplateType = 'plain' | 'group';

@Component({
  selector: 'app-download-prf-template-popup',
  templateUrl: './download-prf-template-popup.component.html',
  styleUrls: ['./download-prf-template-popup.component.scss'],
  standalone: false
})
export class DownloadPrfTemplatePopupComponent implements OnInit {
  @Input() prfyear!: number;

  isCustomer = false;
  templateType: TemplateType | null = null;
  groupcode = new UntypedFormControl('', [Validators.required, Validators.pattern('^\\d{4}?$')]);
  groupname = '';
  isValidating = false;
  validationerror = false;
  pageerror = false;
  error = '';
  istouched = false;
  isGroupValidated = false;
  isDownloading = false;

  constructor(
    public activeModal: NgbActiveModal,
    private readonly companyService: CompanyService,
    private readonly router: Router,
    private readonly appService: AppService,
    private readonly templateDownloadService: PrfTemplateDownloadService
  ) {}

  ngOnInit(): void {
    // Customers can only download the group specific template, so skip the
    // template type selection and pre-fill the group from local storage.
    this.isCustomer = this.appService.hasRole(Role.Customer);
    if (this.isCustomer) {
      this.templateType = 'group';
      const groupcode = localStorage.getItem('groupcode') ?? '';
      this.groupcode.setValue(groupcode);
      if (groupcode) {
        this.runGroupValidation(groupcode);
      }
    }
  }

  selectTemplateType(type: TemplateType): void {
    this.templateType = type;
    if (type === 'plain') {
      this.resetGroupState();
    }
  }

  validateGroup(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.runGroupValidation(value);
  }

  private runGroupValidation(value: string): void {
    this.pageerror = false;
    this.isGroupValidated = false;
    this.groupname = '';

    if (value.length === 4 && /^\d+$/.test(value)) {
      this.istouched = false;
      this.validationerror = false;
      this.isValidating = true;
      this.companyService.validategroup(this.prfyear, this.groupcode.value).subscribe({
        next: (resp) => {
          if (resp['message'] === 'Success') {
            if (resp['status'] === '') {
              this.pageerror = true;
              this.validationerror = true;
              this.error = 'Group does not exist';
              this.groupname = '';
              this.isGroupValidated = false;
            } else {
              this.pageerror = false;
              this.validationerror = false;
              this.groupname = resp['status'];
              this.isGroupValidated = true;
            }
          }
          this.isValidating = false;
        },
        error: () => {
          this.isValidating = false;
          this.router.navigate(['/apifailure']);
        }
      });
    } else {
      this.groupname = '';
      this.validationerror = true;
      this.istouched = true;
      this.isGroupValidated = false;
    }
  }

  validationclick(): void {
    this.istouched = true;
  }

  get canDownload(): boolean {
    if (this.templateType === 'plain') {
      return true;
    }
    if (this.templateType === 'group') {
      return this.isGroupValidated;
    }
    return false;
  }

  downloadTemplate(): void {
    if (!this.canDownload || this.isDownloading) {
      return;
    }
    this.isDownloading = true;

    const download$ =
      this.templateType === 'plain'
        ? this.templateDownloadService.downloadPlainTemplate(this.prfyear)
        : this.templateDownloadService.downloadGroupTemplate(this.prfyear, this.groupcode.value);

    download$
      .then(() => {
        this.isDownloading = false;
        this.activeModal.close('downloaded');
      })
      .catch(() => {
        this.isDownloading = false;
        this.router.navigate(['/apifailure']);
      });
  }

  private resetGroupState(): void {
    this.groupcode.reset('');
    this.groupname = '';
    this.isGroupValidated = false;
    this.validationerror = false;
    this.pageerror = false;
    this.error = '';
    this.istouched = false;
  }
}
